///
/// EarthBrowser
///
/// Created by Matt Giger on 1/2/2026
/// Copyright (c) 2026 Geodata Labs
///

import { Database } from "@/earthbrowser/database/Database"

export interface CloudProperties {
	time: string
	url: string
}

const cBatchSize = 500

export class CloudsTable {
	public static async create(db: Database): Promise<void> {
		await db.execMany([
			{
				sql: `create table if not exists clouds (
					time text primary key,
					url text not null
				)`
			},
			{ sql: "create index if not exists idx_clouds_time on clouds(time desc)" },
		])
	}

	public static async replaceAll(rows: CloudProperties[]): Promise<void> {
		await Database.db.exec("delete from clouds", [], "critical")
		await this.upsert(rows)
	}

	public static async upsert(rows: CloudProperties[]): Promise<void> {
		const statement = `insert into clouds (time, url)
			values (?, ?)
			on conflict(time) do update set
				url = excluded.url`

		for (let i = 0; i < rows.length; i += cBatchSize) {
			const batch = rows.slice(i, i + cBatchSize)
			await Database.db.bulkInsert(statement, batch.map(c => [
				c.time,
				c.url,
			]))
		}
	}

	public static async deleteByTimes(times: string[]): Promise<void> {
		if (times.length === 0) return
		const placeholders = times.map(() => "?").join(",")
		await Database.db.exec(`delete from clouds where time in (${placeholders})`, times, "critical")
	}

	/**
	 * Get all cloud entries ordered by time descending (most recent first)
	 */
	public static async getAll(): Promise<CloudProperties[]> {
		const rows = await Database.db.exec(
			`select time, url from clouds order by time desc`,
			[],
			"low"
		)
		return rows as CloudProperties[]
	}

	/**
	 * Get cloud entries within a time range
	 */
	public static async getByTimeRange(startTime: string, endTime: string): Promise<CloudProperties[]> {
		const rows = await Database.db.exec(
			`select time, url from clouds where time >= ? and time <= ? order by time desc`,
			[startTime, endTime],
			"low"
		)
		return rows as CloudProperties[]
	}

	/**
	 * Get the most recent N cloud entries. High priority: this is the
	 * first-paint read for the cloud layer and must not queue behind the
	 * fresh-install bulk ingests (see RadarTable.getRecent)
	 */
	public static async getRecent(limit: number = 100): Promise<CloudProperties[]> {
		const rows = await Database.db.exec(
			`select time, url from clouds order by time desc limit ?`,
			[limit],
			"high"
		)
		return rows as CloudProperties[]
	}

	/**
	 * Get count of cloud entries
	 */
	public static async getCount(): Promise<number> {
		const rows = await Database.db.exec(
			`select count(*) as count from clouds`,
			[],
			"low"
		) as Array<{ count: number }>
		return rows[0]?.count ?? 0
	}
}

